/**
 * Who may use a node (router docs/PERMISSIONS.md § 4.1, D-20 to D-26, app#375).
 *
 * Besides the node's controllers (its owner, and the Owners and Admins of the node's org), two
 * things give use of a node:
 *
 *  - SHARED WITH THE ORG. This is a setting of the NETWORK: Org (the default) or Private. A node
 *    follows its network (the default) or its controllers set it to Private. A node is shared only
 *    when it follows AND every network it is in is Org. A node in no network is private (D-21),
 *    unless its controllers set it to Org (migration 0257, router#2137): that setting is only for a
 *    node in no network, and in a network the node follows it. Sharing gives `use`, never `root`.
 *  - THE ACCESS LIST: named members of the node's org, at `use` or `root`.
 *
 * The router's tools own every read and write (`node_access_list|set|grant|revoke|request|decide`,
 * `network_update`). These are the pure rules the components render with; `nodeAccess.test.ts`
 * checks them.
 *
 * ⚠⚠ WORDS (D-25): Private is ALWAYS "Private (you and org admins)", never "Private" alone,
 * because Private never excludes the org's Owners and Admins. The second level is ROOT (D-22),
 * never "elevated"; on Windows it means administrator.
 */

import { userText } from './userLabel.ts'

export type NodeSetting = 'follow' | 'private' | 'org'
export type NetworkAccess = 'org' | 'private'
export type AccessLevel = 'use' | 'root'

/** The one word for Private, everywhere in the app (D-25). */
export const PRIVATE_LABEL = 'Private (you and org admins)'
export const SHARED_LABEL = 'Shared with the org'

/**
 * The settings the panel offers. ⚠ The router refuses `org` for a node in a network, so it is
 * offered only for a node in no network, or when it is the setting now (it stays `org` after an
 * attach, and then the node follows its networks).
 */
export const nodeSettingsFor = (view: Pick<NodeAccessView, 'access' | 'networks'>): NodeSetting[] =>
  view.networks.length === 0 || view.access === 'org' ? ['follow', 'private', 'org'] : ['follow', 'private']
export const NETWORK_ACCESSES: NetworkAccess[] = ['org', 'private']

export const NODE_SETTING_LABEL: Record<NodeSetting, string> = {
  follow: 'Follow the network',
  private: PRIVATE_LABEL,
  org: SHARED_LABEL,
}

export const NODE_SETTING_HELP: Record<NodeSetting, string> = {
  follow:
    'The node is shared with the org when every network it is in is Org. Then every member of the org can use it, never as root.',
  private:
    'Only you, the org owners and admins, and the people on the access list can use this node. It still reaches the services of its networks.',
  org: 'For a node in no network. Every member of the org can use it, never as root. When the node joins a network, it follows its networks.',
}

export const NETWORK_ACCESS_LABEL: Record<NetworkAccess, string> = {
  org: 'Org',
  private: PRIVATE_LABEL,
}

export const NETWORK_ACCESS_HELP: Record<NetworkAccess, string> = {
  org: `Every member of the org can use the nodes of this network that follow it: run commands and read and write files. Never as root. A node can still be set to ${PRIVATE_LABEL}.`,
  private:
    'Nobody else can use the nodes of this network: only each node owner, the org owners and admins, and each node’s access list.',
}

export const isNodeSetting = (value: unknown): value is NodeSetting =>
  value === 'follow' || value === 'private' || value === 'org'
export const isNetworkAccess = (value: unknown): value is NetworkAccess => value === 'org' || value === 'private'

/**
 * The plain words a root grant shows before the reader confirms it (§ 4.1: "never silently",
 * D-3). The router refuses a root grant without `confirm_root: true`, so this confirmation is the
 * only way the app sends it.
 */
export const ROOT_WARNING =
  'Root gives everything on the machine (administrator on Windows): every file and every account, the node’s own ' +
  'settings (which only root can change at the machine), and the node’s key. ' +
  'A revoke later closes the noBGP doors only. It does not undo what the person did on the machine, ' +
  'for example an SSH key, a cron job or a tunnel.'

/** What the revoke confirmation says (§ 4.1: "Revoking is not cleanup"). */
export const REVOKE_WARNING =
  'The person is refused from their next call, and a command session they have open is closed. ' +
  'A revoke does not undo what they did on the machine, for example an SSH key, a cron job or a tunnel.'

/**
 * What "Keep this node private" means in the registration pane (D-20), for the picked network's
 * access when it is known (null: not known).
 */
export const keepPrivateCaption = (keepPrivate: boolean, network: NetworkAccess | null): string => {
  // ⚠ In a Private network the box changes nothing TODAY, but it is not empty: a node set to Private
  // stays private when its network later changes to Org, and a node that follows opens (§ 4.1). So
  // the box stays, and says so.
  if (keepPrivate && network === 'private')
    return `The node starts ${PRIVATE_LABEL}. The network is too, so nothing changes now, but the node stays private if the network changes to Org.`
  if (keepPrivate)
    return `The node starts ${PRIVATE_LABEL}: only you, the org owners and admins, and the people you add can use it. It still reaches the services of the network.`
  if (network === 'org') return 'The node follows the network, so every member of the org can use it (never as root).'
  if (network === 'private')
    return `The node follows the network, so it is ${PRIVATE_LABEL} while the network is. If the network changes to Org, every member of the org can use it (never as root).`
  return 'The node follows its network. If the network is shared with the org, every member of the org can use the node (never as root).'
}

// ---- parsing -----------------------------------------------------------------------------------

const str = (value: unknown): string => (typeof value === 'string' ? value : '')

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null

export type AccessEntry = {
  userId: string
  email: string
  /** The member's name (router#1841). Absent when they set none, or on an older router: show the email. */
  name?: string
  level: AccessLevel
  createdAt: string
  /** A root entry while the node reports `allow-root: false`: kept, but it gives nothing beyond use (D-5). */
  blockedByNode: boolean
}

export type AccessRequest = {
  requestId: string
  nodeId: string
  userId: string
  email: string
  /** The member's name (router#1841), when they set one. */
  name?: string
  createdAt: string
}

export type AccessNetwork = { networkId: string; name: string; access: NetworkAccess | '' }

export type NodeAccessView = {
  /** The node's own setting. */
  access: NodeSetting
  /** Whether the node is shared with its org NOW (its setting and every network's access). */
  sharedWithOrg: boolean
  networks: AccessNetwork[]
  /** The node's own `allow-root`. `null`: the agent does not report it. */
  allowRoot: boolean | null
  useEntries: AccessEntry[]
  rootEntries: AccessEntry[]
  pending: AccessRequest[]
  /** The router's notes, for example that a node offers only root (§ 4). Shown next to the setting. */
  notes: string[]
}

const parseEntry = (raw: unknown): AccessEntry | null => {
  const r = record(raw)
  if (!r) return null
  const level = str(r.level)
  if (level !== 'use' && level !== 'root') return null
  return {
    userId: str(r.user_id),
    email: str(r.email),
    ...(str(r.name) ? { name: str(r.name) } : {}),
    level,
    createdAt: str(r.created_at),
    blockedByNode: r.blocked_by_node === true,
  }
}

const parseRequest = (raw: unknown): AccessRequest | null => {
  const r = record(raw)
  if (!r || !str(r.request_id)) return null
  return {
    requestId: str(r.request_id),
    nodeId: str(r.node_id),
    userId: str(r.user_id),
    email: str(r.email),
    ...(str(r.name) ? { name: str(r.name) } : {}),
    createdAt: str(r.created_at),
  }
}

const parseNetworks = (raw: unknown): AccessNetwork[] =>
  (Array.isArray(raw) ? raw : [])
    .map(record)
    .filter((r): r is Record<string, unknown> => r !== null)
    .map(r => ({ networkId: str(r.network_id), name: str(r.name), access: isNetworkAccess(r.access) ? r.access : '' }))

/** `allow_root`, or its old name `allow_admin` (D-23: the router sends both during the transition). */
const allowRootOf = (r: Record<string, unknown>): boolean | null => {
  if (typeof r.allow_root === 'boolean') return r.allow_root
  if (typeof r.allow_admin === 'boolean') return r.allow_admin
  return null
}

/** The router's `notes` on a result envelope: plain sentences for a person, beside the payload. */
export const envelopeNotes = (body: unknown): string[] => {
  const envelope = record(body)
  return (Array.isArray(envelope?.notes) ? envelope.notes : []).filter(
    (n): n is string => typeof n === 'string' && n.length > 0
  )
}

/**
 * Splits a whole result envelope into the tool's payload (nested under `key`) and the router's
 * notes. ⚠ The notes are where the router says what a change did that the reader did not ask for,
 * for example that a node offers only root, so sharing lets nobody do anything there (§ 4).
 * `callTool` drops them; these tools must not.
 */
export const splitEnvelope = (key: string, body: unknown): { payload: unknown; notes: string[] } => {
  const envelope = record(body)
  const nested = envelope ? record(envelope[key]) : null
  return { payload: nested ?? body, notes: envelopeNotes(body) }
}

/**
 * Reads the `node_access_list` answer: the whole envelope (its payload is under `node_access`, not
 * the tool's name), or an already-unwrapped payload.
 *
 * Returns null when the answer has no node setting this app knows. A panel that cannot say who may
 * use the node must not guess. ⚠ The REPLACED choices (just_me, all_org_members, these_members) are
 * not a setting: an older router's answer reads as unknown, never as Follow or Private.
 */
export const parseNodeAccessList = (body: unknown): NodeAccessView | null => {
  const envelope = record(body)
  if (!envelope) return null
  const payload = record(envelope.node_access) ?? envelope
  const access = payload.access
  if (!isNodeSetting(access)) return null
  const entries = (Array.isArray(payload.entries) ? payload.entries : [])
    .map(parseEntry)
    .filter((e): e is AccessEntry => e !== null)
  const pending = (Array.isArray(payload.pending_requests) ? payload.pending_requests : [])
    .map(parseRequest)
    .filter((r): r is AccessRequest => r !== null)
  return {
    access,
    sharedWithOrg: payload.shared_with_org === true,
    networks: parseNetworks(payload.networks),
    allowRoot: allowRootOf(payload),
    // ⚠ Root entries are shown APART from use entries (§ 4.1). A root entry includes use, so it
    // never shows twice.
    useEntries: entries.filter(e => e.level === 'use'),
    rootEntries: entries.filter(e => e.level === 'root'),
    pending,
    notes: envelopeNotes(envelope),
  }
}

// ---- the shared setting ------------------------------------------------------------------------

/** The node's state now, in the panel's words: shared with the org, or Private (you and org admins). */
export const sharedStatus = (view: Pick<NodeAccessView, 'sharedWithOrg'>): string =>
  view.sharedWithOrg ? SHARED_LABEL : PRIVATE_LABEL

/**
 * ⚠ The router gives no name for a network of an org the reader is not a member of (router#2217):
 * the same words as `nodeNetworkLabel`, never the id.
 */
const joinNames = (networks: AccessNetwork[]): string =>
  networks.map(n => n.name || 'a network of another organization').join(', ')

/**
 * Why the node is shared or not, in one sentence (§ 4.1, D-21). It names the networks, because a
 * node that follows is shared only when EVERY network it is in is Org.
 */
export const sharedReason = (view: Pick<NodeAccessView, 'access' | 'sharedWithOrg' | 'networks'>): string => {
  if (view.access === 'private')
    return `This node is set to ${PRIVATE_LABEL}. Only you, the org owners and admins, and its access list can use it.`
  if (view.access === 'org' && view.networks.length === 0)
    return 'This node is in no network, and it is set to be shared with the org. Every member of the org can use it, never as root.'
  if (view.networks.length === 0)
    return `This node follows its network, but it is in no network, so it is ${PRIVATE_LABEL}.`
  const privateNets = view.networks.filter(n => n.access !== 'org')
  if (view.sharedWithOrg || privateNets.length === 0)
    return `This node follows its network (${joinNames(view.networks)}), which is Org. Every member of the org can use it, never as root.`
  const verb = privateNets.length === 1 ? 'is' : 'are'
  return `This node follows its network, and ${joinNames(privateNets)} ${verb} ${PRIVATE_LABEL}. So only you, the org owners and admins, and its access list can use it.`
}

export type NamedNode = { nodeId: string; name: string; reason: string }

const parseNamedNodes = (raw: unknown): NamedNode[] =>
  (Array.isArray(raw) ? raw : [])
    .map(record)
    .filter((r): r is Record<string, unknown> => r !== null)
    .map(r => ({ nodeId: str(r.node_id), name: str(r.name), reason: str(r.reason) }))

/**
 * Reads the `network_update` answer: the access after the call, and the nodes it opened, closed or
 * kept private. ⚠ A change to Org opens only the nodes the caller controls (§ 4.1, router#1877): each
 * other node that follows the network is set to Private, and `kept_private_nodes` names it.
 */
export const parseNetworkUpdate = (
  payload: unknown
): { access: NetworkAccess | ''; opened: NamedNode[]; closed: NamedNode[]; kept: NamedNode[] } => {
  const r = record(payload) ?? {}
  return {
    access: isNetworkAccess(r.access) ? r.access : '',
    opened: parseNamedNodes(r.opened_nodes),
    closed: parseNamedNodes(r.closed_nodes),
    kept: parseNamedNodes(r.kept_private_nodes),
  }
}

const nodesPhrase = (n: number): string => (n === 1 ? 'This node is' : `These ${n} nodes are`)

/**
 * One sentence after a change of the network's access. ⚠ A change from Private to Org OPENS nodes
 * to every member of the org, so the page names every node it opened (§ 4.1: "A change from Private
 * to Org names, in its result, every node it opens to the org").
 */
export const networkAccessSummary = (result: ReturnType<typeof parseNetworkUpdate>): string => {
  if (result.access === 'org') {
    if (result.opened.length === 0 && result.kept.length > 0) return 'The network is Org. No node was opened.'
    if (result.opened.length === 0)
      return `The network is Org. No node was opened: each node is set to ${PRIVATE_LABEL}, or is also in a network that is.`
    return `The network is Org. ${nodesPhrase(result.opened.length)} now shared with the org. Every member of the org can use ${result.opened.length === 1 ? 'it' : 'them'}, never as root:`
  }
  if (result.access === 'private') {
    if (result.closed.length === 0) return `The network is ${PRIVATE_LABEL}. No node was shared, so nothing else changed.`
    return `The network is ${PRIVATE_LABEL}. ${nodesPhrase(result.closed.length)} no longer shared with the org. A member who used one only through sharing is refused on their next call:`
  }
  return 'The network was changed.'
}

/**
 * The sentence above the nodes a change to Org set to Private, because the caller does not control
 * them (§ 4.1, router#1877). Empty when there is none.
 */
export const keptPrivateSummary = (result: ReturnType<typeof parseNetworkUpdate>): string => {
  const n = result.kept.length
  if (result.access !== 'org' || n === 0) return ''
  return (
    `${nodesPhrase(n)} now set to ${PRIVATE_LABEL}, because you do not control ${n === 1 ? 'it' : 'them'}. ` +
    `The node owner, or an org owner or admin, can set ${n === 1 ? 'it' : 'each'} to follow the network:`
  )
}

// ---- the access list ---------------------------------------------------------------------------

/**
 * The arguments of `node_access_grant`. ⚠⚠ `confirm_root` rides ONLY on a root grant, and is true
 * only when the reader confirmed the plain warning (D-3): the router refuses a root grant without it.
 */
export const grantArgs = (
  nodeId: string,
  user: string,
  level: AccessLevel,
  confirmRoot: boolean
): Record<string, unknown> => {
  const args: Record<string, unknown> = { node_id: nodeId, user, level }
  if (level === 'root') args.confirm_root = confirmRoot
  return args
}

/** Why root cannot be granted now, or null when it can. The router refuses the same case. */
export const rootGrantBlocked = (allowRoot: boolean | null): string | null =>
  allowRoot === false
    ? 'This node does not allow root (allow-root is off at the machine), so nobody can run as root there and root cannot be given. The node owner changes this at the machine.'
    : null

export type OrgMemberLike = { userId: string; email: string; name?: string; role: string }

/**
 * The members a controller can add to the list at `level`: members of the node's org who do not
 * already control the node and do not already hold that level. The router refuses a grant to an
 * Owner, an Admin or the node owner ("already controls node"), so offering them is offering a
 * refusal. A second grant REPLACES the level, so a use entry can be raised to root; a root entry is
 * not offered for use, because that grant would LOWER it.
 */
export const grantCandidates = (
  members: OrgMemberLike[],
  view: NodeAccessView | null,
  nodeOwnerId: string | null | undefined,
  level: AccessLevel = 'use'
): OrgMemberLike[] => {
  const held = level === 'root' ? (view?.rootEntries ?? []) : [...(view?.useEntries ?? []), ...(view?.rootEntries ?? [])]
  const listed = new Set(held.map(e => e.userId))
  return members.filter(
    m => m.role !== 'owner' && m.role !== 'admin' && m.userId !== nodeOwnerId && !listed.has(m.userId)
  )
}

/** Reads the `node_access_request` answer (the payload). */
export const parseAccessRequest = (payload: unknown): { requested: number; skipped: NamedNode[] } => {
  const r = record(payload) ?? {}
  return {
    requested: Array.isArray(r.requests) ? r.requests.length : 0,
    skipped: parseNamedNodes(r.skipped),
  }
}

/** What the reader sees after they ask. One node or a whole network. */
export const requestSummary = (requested: number, skipped: number, scope: 'node' | 'network' = 'node'): string => {
  if (requested === 0 && skipped > 0)
    return scope === 'node' ? 'You can already use this node.' : 'You can already use every node of this network.'
  const asked =
    requested === 1
      ? 'Your request is sent. The node owner or an org owner or admin decides.'
      : `${requested} requests are sent, one for each node. The node owner or an org owner or admin decides.`
  if (skipped === 0) return asked
  return `${asked} You can already use ${skipped} other node${skipped === 1 ? '' : 's'}.`
}

/**
 * What the READER holds on a node: `network_directory`'s `my_access` (router#1757). It is the one
 * answer a member who does not control the node can read: `node_access_list` is for controllers
 * only. `none`: the reader sees the node but cannot use it.
 */
export type MyAccess = 'none' | 'use' | 'root' | 'control'

export const isMyAccess = (value: unknown): value is MyAccess =>
  value === 'none' || value === 'use' || value === 'root' || value === 'control'

/** One node of a `network_directory` answer, with what the reader holds on it (null: not said). */
export type DirectoryNodeAccess = { networkId: string; nodeId: string; orgId: string; myAccess: MyAccess | null }

/** Reads each node's `my_access` from the `network_directory` payload. */
export const parseDirectoryAccess = (payload: unknown): DirectoryNodeAccess[] => {
  const networks = record(payload)?.networks
  return (Array.isArray(networks) ? networks : []).flatMap(raw => {
    const network = record(raw)
    const nodes = network?.nodes
    return (Array.isArray(nodes) ? nodes : [])
      .map(record)
      .filter((r): r is Record<string, unknown> => r !== null && str(r.id) !== '')
      .map(r => ({
        networkId: str(network?.id),
        nodeId: str(r.id),
        orgId: str(r.org_id),
        myAccess: isMyAccess(r.my_access) ? r.my_access : null,
      }))
  })
}

/** The ids of the networks a `network_directory` answer holds, nodes or not. */
export const parseDirectoryNetworkIds = (payload: unknown): Set<string> => {
  const networks = record(payload)?.networks
  return new Set((Array.isArray(networks) ? networks : []).map(n => str(record(n)?.id)).filter(id => id !== ''))
}

/**
 * The nodes of the network `networkId` in a directory answer, or null when the answer does not
 * hold that network. ⚠ A name filter returns ONE network, the oldest with that name in the
 * reader's orgs, and a name can repeat. On a newer network with the same name the answer is about
 * another network: that is "not known" (null, which offers the ask), never "no node" ([]).
 */
export const nodesOfNetwork = (
  answer: { networkIds: ReadonlySet<string>; nodes: readonly DirectoryNodeAccess[] },
  networkId: string
): DirectoryNodeAccess[] | null =>
  answer.networkIds.has(networkId) ? answer.nodes.filter(n => n.networkId === networkId) : null

/** A node name as a `path.Match` glob that matches only that name (`network_directory`'s `node_name_glob`). */
export const literalGlob = (name: string): string => name.replace(/[\\*?[\]]/g, c => `\\${c}`)

/** True when the reader can use the node: `use`, `root` or `control` (§ 4: each includes use). */
export const canUseNode = (myAccess: MyAccess | null | undefined): boolean =>
  myAccess === 'use' || myAccess === 'root' || myAccess === 'control'

/**
 * Whether the node page offers "Ask to use this node" (app#423). Not to a reader who can use the
 * node already. ⚠ An answer that is not known (the read failed, an older router, a node in no
 * network) still offers it: the router skips a request from a reader who can use the node, and
 * the page then says so.
 */
export const offerNodeAsk = (myAccess: MyAccess | null | undefined): boolean => !canUseNode(myAccess)

/**
 * Whether the network page offers "Ask to use every node of this network" (app#423): only when
 * the reader cannot use at least one node of their own orgs in it. `null` (the read failed) offers
 * it. The router asks only for the nodes of the reader's orgs, so a node of another org does not
 * count.
 */
export const offerNetworkAsk = (nodes: readonly DirectoryNodeAccess[] | null, readerOrgIds: ReadonlySet<string>): boolean =>
  nodes === null || nodes.some(n => (n.orgId === '' || readerOrgIds.has(n.orgId)) && !canUseNode(n.myAccess))

/** What the node page says, in place of the ask, to a reader who can use the node. */
export const canUseSummary = (myAccess: MyAccess | null | undefined): string =>
  myAccess === 'root' || myAccess === 'control'
    ? 'You can use this node. You can also run as root, if the node allows root.'
    : 'You can use this node.'

export type RevokeTerminal = { serviceId: string; name: string; root: boolean; allOrgMembers: boolean }

/**
 * Reads the `node_access_revoke` answer (the payload). `terminals_naming_member` lists every
 * terminal on the node that still opens for the person: its visitor list names them, or it is open
 * to all org members. A revoke does not edit visitor lists (§ 4.1, D-4), so the page names them for
 * the controller to remove in the same pass.
 */
export const parseRevoke = (
  payload: unknown
): { email: string; name: string; removed: boolean; sharedWithOrg: boolean; terminals: RevokeTerminal[] } => {
  const r = record(payload) ?? {}
  const terminals = (Array.isArray(r.terminals_naming_member) ? r.terminals_naming_member : [])
    .map(record)
    .filter((t): t is Record<string, unknown> => t !== null)
    .map(t => ({
      serviceId: str(t.service_id),
      name: str(t.name),
      // `root`, or its old name `admin` (D-23).
      root: t.root === true || t.admin === true,
      allOrgMembers: t.all_org_members === true,
    }))
  return {
    email: str(r.email),
    name: str(r.name),
    removed: r.removed === true,
    sharedWithOrg: r.shared_with_org === true,
    terminals,
  }
}

/** The sentences after a revoke: what still gives the person access, if anything. */
export const revokeFollowUp = (result: ReturnType<typeof parseRevoke>): string[] => {
  const out: string[] = []
  const who = userText({ name: result.name, email: result.email }, 'This person')
  if (!result.removed) out.push(`${who} had no entry on the access list. Nothing changed.`)
  if (result.sharedWithOrg)
    out.push(
      `This node is shared with the org, so ${who} can still use it (never as root). Set the node to ${PRIVATE_LABEL} to stop that.`
    )
  const n = result.terminals.length
  if (n)
    out.push(
      `${n === 1 ? 'A terminal' : `${n} terminals`} on this node still ${n === 1 ? 'lets' : 'let'} ${who} in as a visitor. ` +
        `A revoke does not change who may visit a terminal. Remove ${who} from ${n === 1 ? 'it' : 'each'}:`
    )
  return out
}

/** How one terminal of a revoke answer is named, with why it still names the person. */
export const revokeTerminalLabel = (t: RevokeTerminal): string =>
  `${t.name || t.serviceId}${t.root ? ' (root terminal)' : ''}${t.allOrgMembers ? ': open to all org members' : ''}`

// ---- the one panel (D-26) ----------------------------------------------------------------------

/**
 * Whether an email matches a visitor pattern: the router's `db.EmailMatches` (an exact address,
 * `*@domain.com`, or `user@*.domain.com`), case-insensitive.
 */
export const emailMatches = (pattern: string, email: string): boolean => {
  const p = pattern.toLowerCase()
  const e = email.toLowerCase()
  if (p === e) return true
  if (!p.includes('*')) return false
  const [pUser, pDomain] = p.split('@')
  const parts = e.split('@')
  if (parts.length !== 2 || pDomain === undefined) return false
  if (pUser === '*' && !pDomain.includes('*')) return parts[1] === pDomain
  if (pDomain.startsWith('*.') && pUser === parts[0]) return parts[1].endsWith(pDomain.slice(1))
  return false
}

/** The service facts the panel reads (from the services store). */
export type PanelService = {
  id: string
  name: string
  title?: string | null
  url: string | null
  /** A terminal that runs as root (`services.admin`). */
  admin: boolean
  authRequired: boolean
  authorizedEmails?: string[] | null
  /** `services.all_org_members` (router 0232, D-17). Absent before it is applied: read as false. */
  allOrgMembers?: boolean
  deletedAt?: string | null
}

/** One entry of a visitor list, as `service_share` names it (router#1841). */
export type ShareVisitor = { email: string; name?: string; member: boolean }

/** A service's visitor list, read from the router (`service_share` action `list`). */
export type ShareList = { authorizedEmails: string[]; visitors: ShareVisitor[] | null; allOrgMembers: boolean }

/**
 * Reads the `service_share` answer (the payload). `visitors` names each entry: a member by name, and
 * whether the address is a member of the node's org. It is null on a router before router#1841, and
 * when the router could not read the members: the panel then shows the addresses alone.
 */
export const parseShareList = (payload: unknown): ShareList | null => {
  const r = record(payload)
  if (!r || !Array.isArray(r.authorized_emails)) return null
  const visitors = Array.isArray(r.visitors)
    ? r.visitors
        .map(record)
        .filter((v): v is Record<string, unknown> => v !== null && !!str(v.email))
        .map(v => ({ email: str(v.email), ...(str(v.name) ? { name: str(v.name) } : {}), member: v.member === true }))
    : null
  return {
    authorizedEmails: r.authorized_emails.filter((e): e is string => typeof e === 'string'),
    visitors,
    allOrgMembers: r.all_org_members === true,
  }
}

export type VisitorWay = {
  kind: 'visitor'
  serviceId: string
  service: string
  /** A terminal (a command on a PTY): its visitor runs a shell on the node (§ 5 Services). */
  terminal: boolean
  rootTerminal: boolean
  /** named: the exact address or pattern is on the list. pattern: a pattern matches the person. org: all org members. public: anyone. */
  via: 'named' | 'pattern' | 'org' | 'public'
  /** The list entry that names them (an address or a pattern). */
  entry?: string
}

export type Way = { kind: 'shared' } | { kind: 'use' } | { kind: 'root'; blocked: boolean } | VisitorWay

export type PersonRow = {
  key: string
  kind: 'org' | 'person' | 'pattern' | 'public'
  /** What the row shows: a member's name, else the email; a pattern; or a group. */
  label: string
  userId?: string
  /** A person's email. Shown on hover when the label is their name (app#401). */
  email?: string
  /** A person's name, when a source gives one. `UserLabel` shows it (app#401). */
  name?: string
  ways: Way[]
}

const serviceLabel = (s: PanelService): string => s.title || s.name || s.id
const isTerminal = (s: PanelService): boolean => !s.url

/**
 * Everyone who can run on the node or visit its services, EACH ONCE, with every way they reach it
 * (D-26). The node's controllers are not listed: they always have access and cannot be removed.
 *
 *  - "Every member of the org": when the node is shared with the org, and for each service open to
 *    all org members.
 *  - Each person: their access-list entry (use, or root and whether the node blocks it), and each
 *    service that names them, exactly or by a pattern. A person on the list is a member of the node's
 *    org, so the org-wide ways are theirs too; so are a visitor's when the router or `members` says
 *    they are a member.
 *  - Each visitor pattern (`*@acme.com`), and "anyone" for a public service.
 *
 * `shareLists` holds the visitor lists read from the router (`service_share` list), by service id.
 * They replace the store's `authorizedEmails` and `allOrgMembers`, and name the visitors. A service
 * with no list read keeps the store's fields.
 *
 * Each way stays its own delegation (D-4): the panel shows them together so a controller never
 * removes one and forgets the other.
 */
export const panelRows = (
  view: Pick<NodeAccessView, 'sharedWithOrg' | 'useEntries' | 'rootEntries'>,
  services: PanelService[],
  members: { userId: string; email: string; name?: string }[] = [],
  shareLists: Record<string, ShareList | undefined> = {}
): PersonRow[] => {
  const orgWays: Way[] = view.sharedWithOrg ? [{ kind: 'shared' }] : []
  const names = new Map<string, string>()
  const orgEmails = new Set<string>()
  for (const m of members) {
    orgEmails.add(m.email.toLowerCase())
    if (m.name) names.set(m.email.toLowerCase(), m.name)
  }
  for (const list of Object.values(shareLists))
    for (const v of list?.visitors ?? []) {
      if (v.member) orgEmails.add(v.email.toLowerCase())
      if (v.name) names.set(v.email.toLowerCase(), v.name)
    }

  const people = new Map<string, PersonRow>()
  const person = (email: string, userId?: string, name?: string): PersonRow => {
    const id = email.toLowerCase()
    if (name) names.set(id, name)
    let row = people.get(id)
    if (!row) {
      row = { key: `person:${id}`, kind: 'person', label: email, email, userId, ways: [] }
      people.set(id, row)
    }
    if (userId && !row.userId) row.userId = userId
    return row
  }

  for (const e of view.rootEntries)
    person(e.email || e.userId, e.userId, e.name).ways.push({ kind: 'root', blocked: e.blockedByNode })
  for (const e of view.useEntries) person(e.email || e.userId, e.userId, e.name).ways.push({ kind: 'use' })

  const patterns = new Map<string, PersonRow>()
  const publicRow: PersonRow = { key: 'public', kind: 'public', label: 'Anyone with the link', ways: [] }

  for (const s of services) {
    if (s.deletedAt) continue
    const list = shareLists[s.id]
    const terminal = isTerminal(s)
    const base = { kind: 'visitor' as const, serviceId: s.id, service: serviceLabel(s), terminal, rootTerminal: terminal && s.admin }
    // A terminal always requires sign-in, whatever the column says (router `RequiresSignIn`).
    if (!s.authRequired && !terminal) {
      publicRow.ways.push({ ...base, via: 'public' })
      continue
    }
    // "All org members" never opens a root terminal (§ 5 Services).
    if ((list ? list.allOrgMembers : s.allOrgMembers) && !base.rootTerminal) orgWays.push({ ...base, via: 'org' })
    for (const entry of list ? list.authorizedEmails : (s.authorizedEmails ?? [])) {
      if (!entry) continue
      if (!entry.includes('*')) {
        person(entry).ways.push({ ...base, via: 'named', entry })
        continue
      }
      // A wildcard names nobody on a root terminal (§ 5 Services).
      if (base.rootTerminal) continue
      const id = entry.toLowerCase()
      let row = patterns.get(id)
      if (!row) {
        row = { key: `pattern:${id}`, kind: 'pattern', label: entry, ways: [] }
        patterns.set(id, row)
      }
      row.ways.push({ ...base, via: 'named', entry })
    }
  }

  for (const [id, row] of people) {
    const name = names.get(id)
    if (name) {
      row.label = name
      row.name = name
    }
    // A pattern also reaches every person it matches, on the services it is on.
    for (const p of patterns.values())
      if (emailMatches(p.label, id))
        for (const w of p.ways) if (w.kind === 'visitor') row.ways.push({ ...w, via: 'pattern' })
    // A member of the node's org reaches it by every org-wide way too.
    if (orgEmails.has(id) || row.ways.some(w => w.kind === 'use' || w.kind === 'root')) row.ways.push(...orgWays)
  }

  const rows: PersonRow[] = []
  if (orgWays.length) rows.push({ key: 'org', kind: 'org', label: 'Every member of the org', ways: orgWays })
  rows.push(...[...people.values()].sort((a, b) => a.label.localeCompare(b.label)))
  rows.push(...[...patterns.values()].sort((a, b) => a.label.localeCompare(b.label)))
  if (publicRow.ways.length) rows.push(publicRow)
  return rows
}

/** One way, in words, for a chip. */
export const wayLabel = (way: Way): string => {
  if (way.kind === 'shared') return 'use: the node is shared with the org'
  if (way.kind === 'use') return 'use: access list'
  if (way.kind === 'root') return way.blocked ? 'root: access list, blocked by the node' : 'root: access list'
  let what = 'service'
  if (way.rootTerminal) what = 'root terminal'
  else if (way.terminal) what = 'terminal'
  let why = ''
  if (way.via === 'org') why = ' (all org members)'
  else if (way.via === 'public') why = ' (public)'
  else if (way.via === 'pattern') why = ` (by ${way.entry})`
  return `visitor: ${what} ${way.service}${why}`
}

/**
 * The panel's groups: root entries, use entries, visitors with no entry, and the rows that are not
 * one person (the org, a pattern, anyone). Root and use are shown APART (§ 4.1), and each person
 * stays in ONE group, so each is shown once (D-26).
 */
export const groupRows = (
  rows: PersonRow[]
): { root: PersonRow[]; use: PersonRow[]; visitors: PersonRow[]; groups: PersonRow[] } => {
  const out = { root: [] as PersonRow[], use: [] as PersonRow[], visitors: [] as PersonRow[], groups: [] as PersonRow[] }
  for (const row of rows) {
    if (row.kind !== 'person') out.groups.push(row)
    else if (row.ways.some(w => w.kind === 'root')) out.root.push(row)
    else if (row.ways.some(w => w.kind === 'use')) out.use.push(row)
    else out.visitors.push(row)
  }
  return out
}

/**
 * Whether to show the reader the controller's panel: the node's owner, or an Owner or Admin of the
 * node's org (§ 3). The org is the node's TENANT, `nodes.owner_org_id` (G-1), never the org of its
 * network.
 *
 *  - The tenant is loaded (a string): the reader's role in THAT org decides.
 *  - The tenant is loaded and null: no org controls the node, so only its owner does (the router's
 *    `nodeTenant` gives no org either).
 *  - The tenant is NOT loaded (undefined; the node store reads it from app#386 on): the reader is
 *    presumed a controller when they are an Owner or Admin of ANY of their orgs. The tenant is one of
 *    their orgs whenever they control the node through it, so this never hides the panel from a
 *    controller. The active org does not stand in: a node in a network of the active org can belong
 *    to another org, and an Admin of THAT org would lose the panel.
 *
 * ⚠ This only picks which view to TRY. The router decides: a read of the list that comes back
 * `forbidden` shows the request view, so the page never shows a list the reader may not change.
 * ⚠ A missing owner or user id is never a match: `null === null` must not make a stranger the owner.
 */
export const presumeController = (
  organizations: { id: string; role: string }[],
  node: { ownerUserId?: string | null; ownerOrgId?: string | null },
  userId: string | null | undefined
): boolean => {
  if (!!node.ownerUserId && !!userId && node.ownerUserId === userId) return true
  const controls = (role: string | undefined) => role === 'owner' || role === 'admin'
  if (node.ownerOrgId === undefined) return organizations.some(o => controls(o.role))
  if (!node.ownerOrgId) return false
  return controls(organizations.find(o => o.id === node.ownerOrgId)?.role)
}

/**
 * Reads the `org_members_list` answer (the payload) into the roster the grant form offers. Null when
 * the answer has no list. ⚠ Only an Owner or Admin may list an org's members, so a Member who owns
 * a node gets a refusal: the form then takes an email, and the router resolves it.
 */
export const parseRoster = (payload: unknown): OrgMemberLike[] | null => {
  const r = record(payload)
  if (!r || !Array.isArray(r.members)) return null
  return r.members
    .map(record)
    .filter((m): m is Record<string, unknown> => m !== null && !!str(m.user_id))
    .map(m => ({ userId: str(m.user_id), email: str(m.email), ...(str(m.name) ? { name: str(m.name) } : {}), role: str(m.role) }))
}
